import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PageContainerAB from '@/components/PageContainerAB/pageContainer';
import IconButton from '@/ctComponents/IconButton';
import FilterPanel from '@/ctComponents/Filter';
import {
  MyAppointments as MyAppointmentsAction,
  getMyAppointmentsReport,
} from '@/actions';
import MyAppointments from '../Appointments';
import ChartSection from './components/ChartSection';
import OriginSourceOverview from './components/OriginSourceOverview';
import DateRangePicker from './components/DateRangePicker';
import { useConditionalyOutsideAlerter } from '@/hocs';
import {
  DEFAULT_FILTERS,
  EMPTY_FILTERS,
  FILTERS_ORIGIN_SOURCE_VALUES_MAP,
  FILTERS_STATUS_VALUES_MAP,
} from './utils/constants';
import { getChartColor } from './utils/colors';
import { createBarDataset, createLineDataset } from './utils/chartHelpers';
import {
  formatDateRangeShort,
  formatDateForInput,
  formatDateForInputTo,
  getCurrentMonth,
  formatDateTimeString,
} from './utils/dateHelpers';
import {
  ORIGIN_SOURCE_DATASETS,
  TERMINAL_DATASETS,
  STATUS_DATASETS,
} from './utils/datasetConfigs';

const Report = () => {
  const [activeChart, setActiveChart] = useState('bar'); // 'bar' or 'line'
  const [splitView, setSplitView] = useState('status'); // 'originSource', 'status', 'terminal'
  const [activeFilter, setActiveFilter] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);
  const [clickedDayIndex, setClickedDayIndex] = useState(null);
  const [clickedBarInfo, setClickedBarInfo] = useState(null);
  const [options, setOptions] = useState({
    open: false,
  });
  const [dateRangePicker, setDateRangePicker] = useState({
    open: false,
    startDate: null,
    endDate: null,
    tempStartDate: null,
    tempEndDate: null,
    filterType: 'DAY',
    tempFilterType: 'DAY',
  });
  const [tempFilters, setTempFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [tableFilter, setTableFilter] = useState(EMPTY_FILTERS);
  const [display, setDisplay] = useState(false);
  const [chartDataFromAPI, setChartDataFromAPI] = useState(null);
  const [isLoadingChartData, setIsLoadingChartData] = useState(false);
  const [dateRangePickerApptsTable, setDateRangePickerApptsTable] = useState({
    startDate: null,
    endDate: null,
    tempStartDate: null,
    tempEndDate: null,
  });
  const [hiddenDatasets, setHiddenDatasets] = useState(new Set());

  // Reset hidden datasets when splitView changes
  useEffect(() => {
    setHiddenDatasets(new Set());
  }, [splitView]);

  const dateRangePickerRef = useRef(null);
  const wrapperRef = useRef(null);
  const filterButtonRef = useRef(null);
  const myAppointmentsRef = useRef(null);

  useConditionalyOutsideAlerter(
    wrapperRef,
    () => setDisplay(false),
    (event) => {
      // Don't close if clicking on the filter button
      if (
        filterButtonRef.current &&
        filterButtonRef.current.contains(event.target)
      ) {
        return false;
      }
      return true;
    }
  );

  const countSelectedFilters = useCallback((filterObj) => {
    return Object.values(filterObj).filter(
      (arr) => Array.isArray(arr) && arr.length > 0
    ).length;
  }, []);

  // Wrapper for setTempFilters to enforce maximum 2 filters
  const handleSetTempFilters = useCallback(
    (updater) => {
      setTempFilters((prev) => {
        const newFilters =
          typeof updater === 'function' ? updater(prev) : updater;

        const currentCount = countSelectedFilters(newFilters);

        if (currentCount > 2) {
          const addedCategory = Object.keys(newFilters).find((key) => {
            const prevArr = prev[key] || [];
            const newArr = newFilters[key] || [];
            return newArr.length > prevArr.length;
          });

          const categoriesToRemove = [
            'terminals',
            'types',
            'shippingLines',
            'originSource',
            'autobookStatus',
            'statuses',
          ];

          let categoryToRemove = null;
          for (const category of categoriesToRemove) {
            if (
              category !== addedCategory &&
              newFilters[category] &&
              newFilters[category].length > 0
            ) {
              categoryToRemove = category;
              break;
            }
          }

          if (categoryToRemove) {
            return {
              ...newFilters,
              [categoryToRemove]: [],
            };
          }
        }

        return newFilters;
      });
    },
    [countSelectedFilters]
  );

  // Handle outside click to close date range picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dateRangePicker.open &&
        dateRangePickerRef.current &&
        !dateRangePickerRef.current.contains(event.target)
      ) {
        setDateRangePicker((prev) => ({ ...prev, open: false }));
      }
    };

    if (dateRangePicker.open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dateRangePicker.open]);

  const handleDateRangeClick = () => {
    setDateRangePicker({
      ...dateRangePicker,
      open: !dateRangePicker.open,
      tempStartDate: dateRangePicker.startDate,
      tempEndDate: dateRangePicker.endDate,
      tempFilterType: dateRangePicker.filterType,
    });
    setOptions({ ...options, open: false });
  };

  const handleDateRangeChange = useCallback((dates) => {
    const [start, end] = dates;
    setDateRangePicker((prev) => ({
      ...prev,
      tempStartDate: start,
      tempEndDate: end,
      tempFilterType: 'DAY',
    }));
    setDateRangePickerApptsTable((prev) => ({
      ...prev,
      tempStartDate: start,
      tempEndDate: end,
    }));
  }, []);

  const handleFilterTypeChange = useCallback((type) => {
    setDateRangePicker((prev) => ({
      ...prev,
      tempFilterType: type,
    }));
  }, []);

  const handleDateRangeReset = useCallback(() => {
    setDateRangePicker((prev) => ({
      ...prev,
      tempStartDate: null,
      tempEndDate: null,
      startDate: null,
      endDate: null,
      filterType: 'DAY',
      tempFilterType: 'DAY',
      open: false,
    }));
    setDateRangePickerApptsTable({
      startDate: null,
      endDate: null,
    });
  }, []);

  const handleDateRangeApply = useCallback(() => {
    setDateRangePicker((prev) => ({
      ...prev,
      startDate: prev.tempStartDate,
      endDate: prev.tempEndDate,
      filterType: prev.tempFilterType,
      open: false,
    }));
    setDateRangePickerApptsTable((prev) => ({
      ...prev,
      startDate: prev.tempStartDate,
      endDate: prev.tempEndDate,
    }));
  }, []);

  const calculateDateRangeFromClickedBar = useCallback(() => {
    const clickedBarDate = clickedBarInfo?.date;
    const filterType = dateRangePicker.filterType;

    const clickedDate = clickedBarDate;
    const currentFilterType = filterType;

    if (!clickedDate) {
      return { startDate: null, endDate: null };
    }

    let startDate, endDate;

    if (currentFilterType === 'DAY') {
      // Parse date string manually to avoid timezone issues
      const parts = clickedDate.split('-');
      if (parts.length < 3) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);

      startDate = new Date(year, month, day, 0, 0, 0, 0);
      endDate = new Date(year, month, day, 23, 59, 59, 999);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { startDate: null, endDate: null };
      }
    } else if (currentFilterType === 'MONTH') {
      const parts = clickedDate.split('-');
      if (parts.length < 2) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      startDate = new Date(year, month, 1, 0, 0, 0, 0);
      endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);
    } else if (currentFilterType === 'WEEK') {
      // Parse date string manually to avoid timezone issues
      const parts = clickedDate.split('-');
      if (parts.length < 3) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);

      startDate = new Date(year, month, day, 0, 0, 0, 0);
      endDate = new Date(year, month, day, 23, 59, 59, 999);
      endDate.setDate(endDate.getDate() + 6);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { startDate: null, endDate: null };
      }
    } else if (
      currentFilterType === 'HOUR' ||
      currentFilterType === 'HOUR4' ||
      currentFilterType === 'HOUR6' ||
      currentFilterType === 'HOUR12'
    ) {
      const parts = clickedDate.split(' ');
      const datePart = parts[0] || clickedDate;
      const timePart = parts[1] || '00';

      // Parse date part manually to avoid timezone issues
      const dateParts = datePart.split('-');
      if (dateParts.length < 3) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(dateParts[2], 10);

      // Determine hours to add based on filter type
      let hoursToAdd = 1; // Default for HOUR
      if (currentFilterType === 'HOUR4') {
        hoursToAdd = 4;
      } else if (currentFilterType === 'HOUR6') {
        hoursToAdd = 6;
      } else if (currentFilterType === 'HOUR12') {
        hoursToAdd = 12;
      }

      let startHour, minutes;

      if (timePart.includes('-')) {
        [startHour, minutes] = timePart.split('-').map(Number);
      } else {
        startHour = Number(timePart);
        minutes = 0;
      }

      if (isNaN(startHour) || (timePart.includes('-') && isNaN(minutes))) {
        startDate = new Date(year, month, day, 0, 0, 0, 0);
        endDate = new Date(year, month, day, hoursToAdd, 0, 0, 0);
      } else {
        startDate = new Date(year, month, day, startHour, minutes || 0, 0, 0);
        endDate = new Date(year, month, day, startHour, minutes || 0, 0, 0);
        endDate.setHours(endDate.getHours() + hoursToAdd);
      }

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { startDate: null, endDate: null };
      }
    } else {
      // Parse date string manually to avoid timezone issues
      const parts = clickedDate.split('-');
      if (parts.length < 3) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);

      startDate = new Date(year, month, day, 0, 0, 0, 0);
      endDate = new Date(year, month, day, 23, 59, 59, 999);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { startDate: null, endDate: null };
      }
    }

    return {
      startDate: formatDateTimeString(startDate),
      endDate: formatDateTimeString(endDate),
    };
  }, [clickedBarInfo, dateRangePicker.filterType]);

  useEffect(() => {
    if (!splitView) return;

    const { startDate, endDate } = calculateDateRangeFromClickedBar();

    if (splitView === 'status') {
      setTableFilter({
        ...filters,
        statuses: clickedBarInfo?.clickedSegment
          ? [
              {
                label: clickedBarInfo.clickedSegment.label,
                value: FILTERS_STATUS_VALUES_MAP.get(
                  clickedBarInfo.clickedSegment.label
                ),
              },
            ]
          : [],
      });
    } else {
      setTableFilter({
        ...filters,
        originSource: clickedBarInfo?.clickedSegment
          ? [
              {
                label: clickedBarInfo.clickedSegment.label,
                value: FILTERS_ORIGIN_SOURCE_VALUES_MAP.get(
                  clickedBarInfo.clickedSegment.label
                ),
              },
            ]
          : [],
      });
    }

    setDateRangePickerApptsTable({
      startDate,
      endDate,
    });
  }, [clickedBarInfo, filters, splitView, calculateDateRangeFromClickedBar]);

  const filterValues = useMemo(
    () => ({
      statuses:
        filters.statuses.length > 0
          ? filters.statuses.map(({ value }) => value)
          : null,
      types:
        filters.types.length > 0
          ? filters.types.map(({ value }) => value)
          : null,
      terminals:
        filters.terminals.length > 0
          ? filters.terminals.map(({ value }) => value)
          : null,
      shipping_lines:
        filters.shippingLines.length > 0
          ? filters.shippingLines.map(({ value }) => value)
          : null,
      origin_sources:
        filters.originSource.length > 0
          ? filters.originSource.map(({ value }) => value)
          : null,
      autobook_statuses:
        filters.autobookStatus.length > 0
          ? filters.autobookStatus.map(({ value }) => value)
          : null,
    }),
    [
      filters.statuses,
      filters.types,
      filters.terminals,
      filters.shippingLines,
      filters.originSource,
      filters.autobookStatus,
    ]
  );

  useEffect(() => {
    const dateFrom =
      dateRangePicker.startDate?.toISOString().split('T')[0] || null;
    const dateTill =
      dateRangePicker.endDate?.toISOString().split('T')[0] || null;
    const timeSplit = dateRangePicker.filterType || 'DAY';

    setIsLoadingChartData(true);
    getMyAppointmentsReport({
      dateFrom,
      dateTill,
      timeSplit,
      ...filterValues,
    })
      .then((response) => {
        if (response?.data?.chartData) {
          setChartDataFromAPI(response.data);
        }
        setIsLoadingChartData(false);
      })
      .catch((error) => {
        console.error('getMyAppointmentsReport error:', error);
        setChartDataFromAPI(null);
        setIsLoadingChartData(false);
      });
  }, [
    dateRangePicker.startDate,
    dateRangePicker.endDate,
    dateRangePicker.filterType,
    filterValues,
  ]);

  const formatDateLabel = useCallback((label, timeSplit) => {
    if (!label || typeof label !== 'string') {
      return '';
    }

    if (timeSplit === 'MONTH') {
      // Format: "2025-12" -> "December"
      const parts = label.split('-');
      if (parts.length < 2) {
        return label;
      }

      return (
        new Date(label).toLocaleString('en-US', { month: 'long' }) || label
      );
    } else if (timeSplit === 'DAY' || timeSplit === 'WEEK') {
      // Format: "2026-01-03" -> "Sat 26"
      const date = new Date(label);
      if (isNaN(date.getTime())) {
        return label;
      }

      const dayNumber = date.getDate();
      return `${dayNumber}`;
    } else if (
      (typeof timeSplit === 'string' && timeSplit.startsWith('HOUR')) ||
      timeSplit === 'HOUR' ||
      timeSplit === 'HOUR4' ||
      timeSplit === 'HOUR6' ||
      timeSplit === 'HOUR12'
    ) {
      // Format: "2026-01-03 00-06" -> "Sat 26, 00-06"
      const parts = label.split(' ');
      const datePart = parts[0] || label;
      const hourPart = parts[1] || '';

      if (!datePart) {
        return label;
      }

      const date = new Date(datePart);
      if (isNaN(date.getTime())) {
        return label;
      }

      const dayNumber = date.getDate();

      const formattedHourPart = hourPart ? hourPart.replace(/-/g, ':') : '';
      return formattedHourPart ? `${formattedHourPart}` : `${dayNumber}`;
    }

    return label;
  }, []);

  const chartData = chartDataFromAPI?.chartData;
  const timeSplit = chartDataFromAPI?.timeSplit || 'DAY';

  const reversedChartData = useMemo(() => {
    return chartData ? chartData.slice().reverse() : null;
  }, [chartData]);

  const chartLabels = useMemo(() => {
    // Show skeleton placeholders when loading
    if (isLoadingChartData) {
      // Use previous data length if available, otherwise use a default number
      const skeletonCount = reversedChartData?.length || 10;
      return Array(skeletonCount).fill('-');
    }

    if (!reversedChartData || reversedChartData.length === 0) {
      return [];
    }

    return reversedChartData
      .map((item) => formatDateLabel(item?.label, timeSplit))
      .filter((label) => label);
  }, [reversedChartData, timeSplit, formatDateLabel, isLoadingChartData]);

  const originalDateLabels = useMemo(() => {
    // Show skeleton placeholders when loading
    if (isLoadingChartData) {
      // Use previous data length if available, otherwise use a default number
      const skeletonCount = reversedChartData?.length || 10;
      return Array(skeletonCount).fill('-');
    }

    if (!reversedChartData || reversedChartData.length === 0) {
      return [];
    }

    return reversedChartData
      .map((item) => item?.label || '')
      .filter((label) => label);
  }, [reversedChartData, isLoadingChartData]);

  const overviewData = useMemo(() => {
    if (!reversedChartData || reversedChartData.length === 0) {
      if (splitView === 'terminal') {
        // Terminal view uses mock data
        const terminalData = {
          trapac: [10, 12, 8, 11, 9, 13, 10],
          matson: [8, 9, 7, 10, 8, 11, 9],
          ssaMarine: [6, 7, 5, 8, 6, 9, 7],
          everport: [5, 6, 4, 7, 5, 8, 6],
          yusen: [4, 5, 3, 6, 4, 7, 5],
        };

        const days = chartLabels.map((label, index) => {
          const trapac = terminalData.trapac[index] || 0;
          const matson = terminalData.matson[index] || 0;
          const ssaMarine = terminalData.ssaMarine[index] || 0;
          const everport = terminalData.everport[index] || 0;
          const yusen = terminalData.yusen[index] || 0;
          const all = trapac + matson + ssaMarine + everport + yusen;

          return {
            all,
            trapac,
            matson,
            ssaMarine,
            everport,
            yusen,
          };
        });

        const total = {
          trapac: terminalData.trapac.reduce((sum, val) => sum + val, 0),
          matson: terminalData.matson.reduce((sum, val) => sum + val, 0),
          ssaMarine: terminalData.ssaMarine.reduce((sum, val) => sum + val, 0),
          everport: terminalData.everport.reduce((sum, val) => sum + val, 0),
          yusen: terminalData.yusen.reduce((sum, val) => sum + val, 0),
        };
        total.all =
          total.trapac +
          total.matson +
          total.ssaMarine +
          total.everport +
          total.yusen;

        return { total, days, type: 'terminal' };
      }
      return null;
    }

    if (splitView === 'status') {
      const days = reversedChartData.map((item) => {
        const statuses = item.statuses || {};
        const scheduled = statuses.Scheduled || 0;
        const expired = statuses.Expired || 0;
        const used = statuses.Used || 0;
        const cancelled = statuses.Canceled || 0;
        const rescheduled = statuses.Rescheduled || 0;
        const rejected = statuses.Rejected || 0;
        const pending = statuses.Pending || 0;
        const all =
          scheduled +
          expired +
          used +
          cancelled +
          rescheduled +
          rejected +
          pending;

        return {
          all,
          used,
          cancelled,
          expired,
          rescheduled,
          scheduled,
        };
      });

      const total = days.reduce(
        (acc, day) => ({
          scheduled: acc.scheduled + day.scheduled,
          expired: acc.expired + day.expired,
          used: acc.used + day.used,
          cancelled: acc.cancelled + day.cancelled,
          rescheduled: acc.rescheduled + day.rescheduled,
        }),
        { scheduled: 0, expired: 0, used: 0, cancelled: 0, rescheduled: 0 }
      );
      total.all =
        total.scheduled +
        total.expired +
        total.used +
        total.cancelled +
        total.rescheduled;

      return { total, days, type: 'status' };
    } else if (splitView === 'originSource') {
      const days = reversedChartData.map((item) => {
        const origins = item.origins || {};
        const manual = origins.Manual || 0;
        const external = origins.External || 0;
        const autobook = origins.Autobook || 0;
        const all = manual + external + autobook;

        return {
          all,
          manual,
          external,
          autobook,
        };
      });

      const total = days.reduce(
        (acc, day) => ({
          manual: acc.manual + day.manual,
          external: acc.external + day.external,
          autobook: acc.autobook + day.autobook,
        }),
        { manual: 0, external: 0, autobook: 0 }
      );
      total.all = total.manual + total.external + total.autobook;

      return { total, days, type: 'originSource' };
    }

    return null;
  }, [splitView, chartLabels, reversedChartData]);

  const getChartDatasetColor = useCallback(
    (label) => getChartColor(label, splitView),
    [splitView]
  );

  const barChartData = useMemo(() => {
    if (!overviewData?.days) {
      return { labels: chartLabels, datasets: [] };
    }

    let datasetConfigs = [];
    if (splitView === 'originSource') {
      datasetConfigs = ORIGIN_SOURCE_DATASETS;
    } else if (splitView === 'terminal') {
      datasetConfigs = TERMINAL_DATASETS;
    } else {
      datasetConfigs = STATUS_DATASETS;
    }

    const datasets = datasetConfigs.map((config) => {
      const dataset = createBarDataset(
        config.label,
        getChartDatasetColor(config.label),
        overviewData.days.map((day) => day[config.key] || 0),
        chartLabels.length
      );
      // Use Chart.js built-in hidden property to show/hide datasets
      if (hiddenDatasets.has(config.label)) {
        dataset.hidden = true;
      }
      return dataset;
    });

    return { labels: chartLabels, datasets };
  }, [
    chartLabels,
    overviewData,
    splitView,
    getChartDatasetColor,
    hiddenDatasets,
  ]);

  const lineChartData = useMemo(() => {
    if (!overviewData?.days) {
      return { labels: chartLabels, datasets: [] };
    }

    let datasetConfigs = [];
    if (splitView === 'originSource') {
      datasetConfigs = ORIGIN_SOURCE_DATASETS;
    } else if (splitView === 'terminal') {
      datasetConfigs = TERMINAL_DATASETS;
    } else {
      datasetConfigs = STATUS_DATASETS;
    }

    const datasets = datasetConfigs.map((config) => {
      const dataset = createLineDataset(
        config.label,
        getChartDatasetColor(config.label),
        overviewData.days.map((day) => day[config.key] || 0)
      );
      // Use Chart.js built-in hidden property instead of filtering
      if (hiddenDatasets.has(config.label)) {
        dataset.hidden = true;
      }
      return dataset;
    });

    return { labels: chartLabels, datasets };
  }, [
    chartLabels,
    overviewData,
    splitView,
    getChartDatasetColor,
    hiddenDatasets,
  ]);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setTempFilters(EMPTY_FILTERS);
    localStorage.removeItem('report-filters');
    setDisplay(false);
  }, []);

  const getFilter = () => {
    MyAppointmentsAction({
      statuses: [],
      types: [],
      terminals: [],
      shipping_lines: [],
      container_name: [],
    }).then((res) => {
      const transformedArray = [
        {
          category: 'statuses',
          title: 'Status',
          options:
            res.data.statuses?.map((s) => ({
              label: s.title,
              value: s.value,
            })) || [],
        },
        {
          category: 'terminals',
          title: 'Terminal',
          options:
            res.data.terminals?.map((t) => ({
              label: t.slug,
              value: t.id,
            })) || [],
        },
        {
          category: 'types',
          title: 'Type',
          options:
            res.data.types?.map((t) => ({
              label: t.title,
              value: t.value,
            })) || [],
        },
        {
          category: 'shippingLines',
          title: 'SSL',
          options:
            res.data.shippingLines?.map((s) => ({
              label: s.name,
              value: s.id,
            })) || [],
        },
        {
          category: 'originSource',
          title: 'Origin Source',
          options: [
            {
              label: 'Autobook',
              value: 'AUTOBOOK',
            },
            {
              label: 'Manual',
              value: 'MANUAL',
            },
            {
              label: 'External',
              value: 'EXTERNAL',
            },
          ],
        },
        {
          category: 'autobookStatus',
          title: 'Autobook Status',
          options: [
            {
              label: 'On',
              value: 'ON',
            },
            {
              label: 'Off',
              value: 'OFF',
            },
          ],
        },
      ];
      setOptions(transformedArray);
    });
  };

  return (
    <PageContainerAB
      minWidth={860}
      title="Performance Report"
      mainPage={true}
      basePath="/my-appointment">
      <div className="">
        <div className="mb-6">
          <h2 className="text-[28px] font-averta-bold text-primary-text font-bold mb-2">
            Container Appointment Report
          </h2>
          <p className="text-base font-averta text-inactive-text mt-2">
            Track and analyze the status of your container appointments to gain
            insights and manage them efficiently.
          </p>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1">
            {DEFAULT_FILTERS.map((filter, index) => (
              <span
                key={index}
                className={`  ${
                  index === activeFilter
                    ? 'bg-inactive-text text-white'
                    : 'bg-transparent text-inactive-text'
                } text-11 cursor-pointer flex items-center justify-center font-bold border-inactive-text border border-solid rounded-full text-center h-7 px-3`}
                onClick={() => {
                  setActiveFilter(index);
                  setFilters({
                    ...filters,
                    statuses:
                      filter === 'Overview'
                        ? []
                        : [
                            {
                              label: filter,
                              value: FILTERS_STATUS_VALUES_MAP.get(filter),
                            },
                            ...(filter === 'Scheduled'
                              ? [
                                  {
                                    label: 'Rescheduled',
                                    value: 'EDITED',
                                  },
                                ]
                              : []),
                          ],
                  });

                  if (filter === 'Overview') setSplitView('status');
                  else setSplitView('originSource');
                }}>
                {filter}
              </span>
            ))}
          </div>
          <div className="relative flex flex-wrap flex-col sm:flex-row gap-1 lg:gap-3 w-full lg:w-auto justify-start">
            <div className="relative">
              <IconButton
                ref={filterButtonRef}
                active={display}
                text="Filter"
                icon="filter"
                onClick={() => {
                  setDisplay((pre) => !pre);
                  if (options.length > 0) {
                    return;
                  }
                  getFilter();
                }}
                style={{
                  height: 40,
                }}
              />
              {display && (
                <div
                  className="absolute top-11 z-999 right-0 "
                  ref={wrapperRef}>
                  <FilterPanel
                    includeLabel={true}
                    lastItemTitle="Autobook Status"
                    options={options}
                    filters={tempFilters}
                    setFilters={handleSetTempFilters}
                    resetFilters={resetFilters}
                    apply={() => {
                      setFilters(tempFilters);
                      setTableFilter(tempFilters);
                      setDisplay(false);
                    }}
                    setDisplay={setDisplay}
                  />
                </div>
              )}
            </div>

            <DateRangePicker
              dateRangePicker={dateRangePicker}
              setDateRangePicker={setDateRangePicker}
              dateRangePickerRef={dateRangePickerRef}
              handleDateRangeClick={handleDateRangeClick}
              handleDateRangeChange={handleDateRangeChange}
              handleFilterTypeChange={handleFilterTypeChange}
              handleDateRangeReset={handleDateRangeReset}
              handleDateRangeApply={handleDateRangeApply}
              formatDateRangeShort={formatDateRangeShort}
              formatDateForInput={formatDateForInput}
              formatDateForInputTo={formatDateForInputTo}
            />
          </div>
        </div>
        <div className={`flex flex-col xl:flex-row gap-4 my-6`}>
          <ChartSection
            activeChart={activeChart}
            setActiveChart={setActiveChart}
            splitView={splitView}
            setSplitView={setSplitView}
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
            barChartData={barChartData}
            lineChartData={lineChartData}
            getCurrentMonth={getCurrentMonth}
            selectedDayIndex={selectedDayIndex}
            setSelectedDayIndex={setSelectedDayIndex}
            setClickedDayIndex={setClickedDayIndex}
            clickedBarInfo={clickedBarInfo}
            setClickedBarInfo={setClickedBarInfo}
            activeFilter={activeFilter}
            defaultFilters={DEFAULT_FILTERS}
            dateRangePicker={dateRangePicker}
            myAppointmentsRef={myAppointmentsRef}
            filters={filters}
            setTempFilters={setTempFilters}
            setFilters={setFilters}
            originalDateLabels={originalDateLabels}
            chartLabels={chartLabels}
            setActiveFilter={setActiveFilter}
            isLoadingChartData={isLoadingChartData}
          />

          {!isFullscreen && (
            <div
              key="right-panel"
              className="flex flex-col gap-4 w-full xl:w-1/3">
              <OriginSourceOverview
                key={splitView}
                overviewData={overviewData}
                splitView={splitView}
                activeFilter={activeFilter}
                defaultFilters={DEFAULT_FILTERS}
                selectedDayIndex={selectedDayIndex}
                chartLabels={chartLabels}
                clickedBarInfo={clickedBarInfo}
                originalDateLabels={originalDateLabels}
                hiddenDatasets={hiddenDatasets}
                setHiddenDatasets={setHiddenDatasets}
              />
            </div>
          )}
        </div>
        <div className="mt-6" ref={myAppointmentsRef}>
          <MyAppointments
            fromReports={true}
            reportFilters={tableFilter}
            dateRangePicker={dateRangePickerApptsTable}
            selectedDayIndex={clickedDayIndex}
            chartLabels={chartLabels}
          />
        </div>
      </div>
    </PageContainerAB>
  );
};

export default Report;
